/**
 * Formats WYSIWYG content with enhanced styling and image functionality
 * @param {string} htmlContent - Raw HTML content from WYSIWYG
 * @param {string} language - Current language for translations
 * @returns {string} Formatted HTML with enhanced styling
 */
export function formatContent(htmlContent, language = "en") {
  if (!htmlContent) return "";

  let formattedContent = htmlContent;

  // Add responsive image classes and click handlers
  formattedContent = formattedContent.replace(
    /<img([^>]*)>/gi,
    (match, attributes) => {
      // Extract src and alt attributes
      const srcMatch = attributes.match(/src=["']([^"']+)["']/);
      const altMatch = attributes.match(/alt=["']([^"']+)["']/);
      const src = srcMatch ? srcMatch[1] : "";
      const alt = altMatch ? altMatch[1] : "";

      // Add responsive classes and click handler
      return `<img${attributes} class="wysiwyg-image cursor-pointer hover:opacity-90 transition-opacity duration-200" data-src="${src}" data-alt="${alt}" />`;
    }
  );

  // Enhance video elements with responsive styling and optimization
  formattedContent = formattedContent.replace(
    /<video([^>]*)>/gi,
    (match, attributes) => {
      // Add responsive classes and video optimization attributes
      return `<video${attributes} class="wysiwyg-video max-w-full h-auto rounded-lg shadow-md my-6" preload="metadata" controls controlsList="nodownload" />`;
    }
  );

  // Enhance headings with better spacing and typography
  formattedContent = formattedContent.replace(
    /<h([1-6])([^>]*)>/gi,
    (match, level, attributes) => {
      const baseClasses = "font-bold text-gray-900 mb-4 mt-8";
      const levelClasses = {
        1: "text-3xl",
        2: "text-2xl",
        3: "text-xl",
        4: "text-lg",
        5: "text-base",
        6: "text-sm",
      };
      return `<h${level}${attributes} class="${baseClasses} ${
        levelClasses[level] || levelClasses[3]
      }">`;
    }
  );

  // Enhance paragraphs with better spacing and typography
  formattedContent = formattedContent.replace(
    /<p([^>]*)>/gi,
    '<p$1 class="text-gray-700 leading-relaxed mb-6">'
  );

  // Enhance lists with better spacing and bullets
  formattedContent = formattedContent.replace(
    /<ul([^>]*)>/gi,
    '<ul$1 class="list-disc list-inside mb-6 space-y-2 text-gray-700">'
  );
  formattedContent = formattedContent.replace(
    /<ol([^>]*)>/gi,
    '<ol$1 class="list-decimal list-inside mb-6 space-y-2 text-gray-700">'
  );

  // Fix list items with headings inside them
  formattedContent = formattedContent.replace(
    /<li([^>]*)>\s*<h([1-6])([^>]*)>(.*?)<\/h[1-6]>\s*<\/li>/gi,
    (match, liAttrs, headingLevel, headingAttrs, headingContent) => {
      // Convert the heading to a span with heading styles
      const headingClasses = {
        1: "text-3xl font-bold text-gray-900",
        2: "text-2xl font-bold text-gray-900",
        3: "text-xl font-bold text-gray-900",
        4: "text-lg font-bold text-gray-900",
        5: "text-base font-bold text-gray-900",
        6: "text-sm font-bold text-gray-900",
      };
      const classes = headingClasses[headingLevel] || headingClasses[3];
      return `<li${liAttrs}><span class="${classes} inline-block">${headingContent}</span></li>`;
    }
  );

  // Enhance blockquotes
  formattedContent = formattedContent.replace(
    /<blockquote([^>]*)>/gi,
    '<blockquote$1 class="border-l-4 border-primary pl-6 py-4 my-6 bg-gray-50 italic text-gray-700">'
  );

  // Enhance links
  formattedContent = formattedContent.replace(
    /<a([^>]*)>/gi,
    '<a$1 class="text-primary hover:text-primary/80 underline transition-colors duration-200">'
  );

  // Enhance strong and emphasis
  formattedContent = formattedContent.replace(
    /<strong([^>]*)>/gi,
    '<strong$1 class="font-semibold text-gray-900">'
  );
  formattedContent = formattedContent.replace(
    /<em([^>]*)>/gi,
    '<em$1 class="italic text-gray-800">'
  );

  // Enhance code blocks
  formattedContent = formattedContent.replace(
    /<code([^>]*)>/gi,
    '<code$1 class="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">'
  );

  // Enhance pre blocks
  formattedContent = formattedContent.replace(
    /<pre([^>]*)>/gi,
    '<pre$1 class="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono text-gray-800 mb-6">'
  );

  // Add responsive container classes
  formattedContent = formattedContent.replace(
    /<div([^>]*)>/gi,
    '<div$1 class="wysiwyg-content">'
  );

  return formattedContent;
}

/**
 * Extracts all image URLs from formatted content
 * @param {string} htmlContent - Formatted HTML content
 * @returns {Array} Array of image URLs
 */
export function extractImagesFromContent(htmlContent) {
  if (!htmlContent) return [];

  const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  const images = [];
  let match;

  while ((match = imgRegex.exec(htmlContent)) !== null) {
    images.push(match[1]);
  }

  return images;
}

/**
 * Creates a lightbox modal for images
 * @param {string} imageSrc - Image source URL
 * @param {string} imageAlt - Image alt text
 */
export function createImageModal(imageSrc, imageAlt) {
  // Remove existing modal if any
  const existingModal = document.getElementById("image-modal");
  if (existingModal) {
    existingModal.remove();
  }

  const modal = document.createElement("div");
  modal.id = "image-modal";
  modal.className =
    "fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm";
  modal.innerHTML = `
    <div class="relative max-w-7xl max-h-full p-4">
      <button 
        onclick="this.closest('#image-modal').remove()" 
        class="absolute top-4 right-4 z-10 text-white hover:text-gray-300 transition-colors duration-200"
      >
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
      
      <div class="flex flex-col items-center space-y-4">
        <img 
          src="${imageSrc}" 
          alt="${imageAlt}" 
          class="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
        />
        ${
          imageAlt
            ? `<p class="text-white text-center text-lg">${imageAlt}</p>`
            : ""
        }
      </div>
      
      <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-4">
        <button 
          onclick="zoomImage(0.8)" 
          class="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200"
        >
          Zoom Out
        </button>
        <button 
          onclick="zoomImage(1.2)" 
          class="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200"
        >
          Zoom In
        </button>
        <button 
          onclick="resetZoom()" 
          class="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200"
        >
          Reset
        </button>
      </div>
    </div>
  `;

  // Add zoom functionality
  let currentZoom = 1;
  window.zoomImage = (factor) => {
    const img = modal.querySelector("img");
    currentZoom *= factor;
    currentZoom = Math.max(0.5, Math.min(3, currentZoom)); // Limit zoom between 0.5x and 3x
    img.style.transform = `scale(${currentZoom})`;
  };

  window.resetZoom = () => {
    const img = modal.querySelector("img");
    currentZoom = 1;
    img.style.transform = "scale(1)";
  };

  // Close modal on background click
  modal.addEventListener("click", (e) => {
    if (e.target === modal) {
      modal.remove();
    }
  });

  // Close modal on Escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && document.getElementById("image-modal")) {
      document.getElementById("image-modal").remove();
    }
  });

  document.body.appendChild(modal);
}

// Make functions globally available for onclick handlers
window.openImageModal = createImageModal;
